import {
    Button,
    Col,
    DatePicker,
    Divider,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Row,
    Select,
    TimePicker,
    Checkbox
} from "antd";
import {useFormik} from "formik";
import React, {useState, useMemo} from "react";
import moment from "moment";
import locale from "antd/es/date-picker/locale/tr_TR";
import {useStore} from "../../../../../hooks/useStore";
import {
    courierBonusCampaignStatuses,
    courierBonusCampaignTypes,
    guaranteeCalculationFactors
} from "../../../../../constants/constants";
import MinimumPackagesInput from "../minimumPackagesInput/MinimumPackagesInput";
import {CampaignDaysInput} from "../campaignDaysInput/CampaignDaysInput";
import {useFetch} from "../../../../../hooks/useFetch";
import {CreateCampaignButton} from "./styles/CampaignModal.styled";

const MODES = {
    CREATE: "create",
    EDIT: "edit"
};
export const CampaignModal = ({
    poolId = undefined,
    subCompanyId = undefined,
    campaignType = courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value,
    refetch,
    campaign = undefined,
    mode = MODES.CREATE, // create or edit
    buttonLabel = "Kurye Bonus Kampanyası Oluştur",
    modalTitle = "Kurye Bonus Kampanyası Oluştur",
    okButtonLabel = "Oluştur"
}) => {
    const stores = useStore();
    const [confirmLoading, setConfirmLoading] = useState(false);
    const [visibility, setVisibility] = useState(false);
    const defaultConditions = {
        [courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value]: {
            courierAmountsByPackageCount: [
                {
                    minimumPackageCount: 1,
                    courierAmount: 0
                }
            ]
        },
        [courierBonusCampaignTypes.HOURLY_FEE.value]: {
            companyAmount: 0,
            courierAmount: 0
        },
        [courierBonusCampaignTypes.HOURLY_GUARANTEE_FEE.value]: {
            courierGuaranteeAmount: 0,
            chargeSubCompany: false,
            subCompanyGuaranteeAmount: 0,
            subCompanyId: undefined,
            calculationFactors: []
        }
    };
    const initialValues = {
        name: campaign?.name || "",
        poolId: campaign?.poolId || poolId,
        subCompanyId: campaign?.subCompanyId || subCompanyId,
        type: campaign?.type || campaignType,
        status: campaign?.status || courierBonusCampaignStatuses.ACTIVE.value,
        startDate: campaign?.startDate ? moment(campaign?.startDate) : undefined,
        endDate: campaign?.endDate ? moment(campaign?.endDate) : undefined,
        startHour: campaign?.startHour ? moment(campaign?.startHour, "HH:mm") : undefined,
        endHour: campaign?.endHour ? moment(campaign?.endHour, "HH:mm") : undefined,
        days: campaign?.days || [],
        conditions:
            mode === MODES.EDIT && campaign?.conditions ? campaign?.conditions : defaultConditions
    };

    const [poolLoading, pool] = useFetch({
        fetchFn: () => stores.poolStore.getPool({poolId: initialValues.poolId}),
        dependencies: [initialValues.poolId],
        initialCall: !!initialValues.poolId,
        condition: !!initialValues.poolId
    });
    const poolSubCompanyIds = useMemo(
        () => pool?.subCompanies?.map(subCompany => subCompany._id) || [],
        [pool?.subCompanies]
    );

    const [subCompaniesLoading, subCompanies] = useFetch({
        defaultDataValue: [],
        fetchFn: () =>
            stores.subCompanyStore.listWithCredit({
                subCompanyIds: poolSubCompanyIds
            }),
        dependencies: [poolSubCompanyIds],
        initialCall: poolSubCompanyIds?.length > 0,
        transform: result => {
            const data = result?.data || result;
            return Array.isArray(data) ? data : [];
        }
    });

    const formik = useFormik({
        initialValues,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
        validate: values => {
            const errors = {};
            if (values.type === courierBonusCampaignTypes.HOURLY_GUARANTEE_FEE.value) {
                const conditions = values?.conditions?.[values.type];

                if (conditions?.chargeSubCompany) {
                    if (
                        !conditions?.subCompanyGuaranteeAmount ||
                        conditions?.subCompanyGuaranteeAmount <= 0
                    ) {
                        if (!errors.conditions) errors.conditions = {};
                        if (!errors.conditions[values.type]) errors.conditions[values.type] = {};
                        errors.conditions[values.type].subCompanyGuaranteeAmount =
                            "Müşteri Garanti Ücreti girilmelidir";
                    }

                    if (!conditions?.subCompanyId) {
                        if (!errors.conditions) errors.conditions = {};
                        if (!errors.conditions[values.type]) errors.conditions[values.type] = {};
                        errors.conditions[values.type].subCompanyId =
                            "Ücret Kesilecek Alt Firma seçilmelidir";
                    }
                }
            }

            return errors;
        },
        onSubmit: async values => {
            setConfirmLoading(true);

            try {
                const payload = {
                    name: values?.name || "",
                    poolId: values?.poolId,
                    subCompanyId: values?.subCompanyId,
                    type: values?.type,
                    status: values?.status,
                    startDate: values?.startDate
                        ? moment(values?.startDate).format("YYYY-MM-DD HH:mm")
                        : undefined,
                    endDate: values?.endDate
                        ? moment(values?.endDate).format("YYYY-MM-DD HH:mm")
                        : undefined,
                    startHour: values?.startHour
                        ? moment(values?.startHour).format("HH:mm")
                        : undefined,
                    endHour: values?.endHour ? moment(values?.endHour).format("HH:mm") : undefined,
                    days: values?.days,
                    conditions: {
                        [values?.type]: values?.conditions?.[values?.type] || {}
                    }
                };
                if (mode === MODES.CREATE) {
                    try {
                        const createCampaignResult = await stores.courierBonusCampaignStore.createCourierBonusCampaign(
                            payload
                        );
                        if (createCampaignResult?.success) {
                            onClose();
                            refetch();
                        }
                    } catch (error) {
                        console.error(error);
                    }
                }
                if (mode === MODES.EDIT) {
                    try {
                        const updateCampaignResult = await stores.courierBonusCampaignStore.updateCourierBonusCampaign(
                            {...payload, _id: campaign?._id}
                        );
                        if (updateCampaignResult?.success) {
                            onClose();
                            refetch();
                        }
                    } catch (error) {
                        console.error(error);
                    }
                }
            } catch (error) {
                console.error("Kampanya oluşturulurken hata:", error);
            } finally {
                setConfirmLoading(false);
            }
        }
    });
    const {values, handleSubmit, handleChange, setFieldValue, setValues, resetForm} = formik;

    const onClose = () => {
        resetForm();
        setVisibility(false);
        setValues(initialValues);
    };

    const deleteCampaign = async () => {
        setConfirmLoading(true);
        try {
            const deletedCampaignResult = await stores.courierBonusCampaignStore.deleteCourierBonusCampaign(
                {_id: campaign?._id}
            );
            if (deletedCampaignResult?.success) {
                onClose();
                refetch();
            }
        } catch (error) {
            console.error("Kampanya silinirken hata:", error);
        } finally {
            setConfirmLoading(false);
        }
    };
    return (
        <React.Fragment>
            <CreateCampaignButton type="primary" onClick={() => setVisibility(true)}>
                {buttonLabel}
            </CreateCampaignButton>
            <Modal
                width={1000}
                title={modalTitle}
                visible={visibility}
                confirmLoading={confirmLoading}
                onCancel={onClose}
                maskClosable={false}
                footer={
                    <Row justify={mode === MODES.EDIT ? "space-between" : "end"}>
                        {mode === MODES.EDIT && (
                            <Popconfirm
                                title="Kampanyayı silmek istediğinize emin misiniz?"
                                onConfirm={deleteCampaign}
                                okText="Evet"
                                cancelText="Hayır"
                            >
                                <Button loading={confirmLoading} disabled={confirmLoading}>
                                    Sil
                                </Button>
                            </Popconfirm>
                        )}
                        <Col>
                            <Button onClick={onClose} disabled={confirmLoading}>
                                İptal
                            </Button>
                            <Popconfirm
                                title="Kampanya oluşturmak istediğinize emin misiniz?"
                                onConfirm={handleSubmit}
                                okText="Evet"
                                cancelText="Hayır"
                            >
                                <Button loading={confirmLoading} disabled={confirmLoading}>
                                    {okButtonLabel}
                                </Button>
                            </Popconfirm>
                        </Col>
                    </Row>
                }
                style={{top: 20}}
            >
                <Form layout="vertical">
                    <Row gutter={16}>
                        <Col sm={12} xs={24}>
                            <Form.Item label="Kampanya Adı">
                                <Input
                                    name="name"
                                    value={values.name}
                                    onChange={e => setFieldValue("name", e.target.value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col sm={12} xs={24}>
                            <Form.Item
                                label="Kampanya Tipi"
                                validateStatus={formik.errors.type ? "error" : ""}
                                help={formik.errors.type}
                            >
                                <Select
                                    name="type"
                                    value={values.type}
                                    onChange={value => setFieldValue("type", value)}
                                    disabled={mode !== MODES.CREATE}
                                >
                                    {Object.values(courierBonusCampaignTypes).map(type => (
                                        <Select.Option key={type.value} value={type.value}>
                                            {type.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col sm={12} xs={24}>
                            <Form.Item label="Kampanya Başlangıç Tarihi">
                                <DatePicker
                                    style={{width: "100%"}}
                                    name="startDate"
                                    type="date"
                                    value={values.startDate ? moment(values.startDate) : ""}
                                    format={"DD.MM.YYYY HH:mm"}
                                    showTime={true}
                                    showNow={false}
                                    onSelect={startDate =>
                                        setFieldValue(
                                            "startDate",
                                            startDate
                                                .minute(0)
                                                .second(0)
                                                .millisecond(0)
                                        )
                                    }
                                    disabledDate={current =>
                                        values.endDate
                                            ? moment(current).isAfter(
                                                  moment(values.endDate).startOf("day")
                                              )
                                            : false
                                    }
                                    disabledTime={() => ({
                                        disabledMinutes: () => {
                                            const minutesArr = Array.from(
                                                {length: 60},
                                                (_, i) => i
                                            );
                                            return minutesArr.filter(minute => minute !== 0);
                                        }
                                    })}
                                    locale={locale}
                                />
                            </Form.Item>
                        </Col>
                        <Col sm={12} xs={24}>
                            <Form.Item label="Kampanya Bitiş Tarihi">
                                <DatePicker
                                    style={{width: "100%"}}
                                    name="endDate"
                                    type="date"
                                    value={values.endDate ? moment(values.endDate) : ""}
                                    format={"DD.MM.YYYY HH:mm"}
                                    showTime={true}
                                    showNow={false}
                                    onSelect={endDate =>
                                        setFieldValue(
                                            "endDate",
                                            endDate
                                                .minute(0)
                                                .second(0)
                                                .millisecond(0)
                                        )
                                    }
                                    disabledDate={current =>
                                        values.startDate
                                            ? moment(values.startDate).isAfter(
                                                  moment(current).startOf("day")
                                              )
                                            : false
                                    }
                                    disabledTime={() => ({
                                        disabledMinutes: () => {
                                            const minutesArr = Array.from(
                                                {length: 60},
                                                (_, i) => i
                                            );
                                            return minutesArr.filter(minute => minute !== 0);
                                        }
                                    })}
                                    locale={locale}
                                />
                            </Form.Item>
                        </Col>
                        <Col sm={6} xs={24}>
                            <Form.Item label="Başlangıç Saati">
                                <TimePicker
                                    style={{width: "100%"}}
                                    name="startHour"
                                    value={
                                        values.startHour ? moment(values.startHour, "HH:mm") : ""
                                    }
                                    showNow={false}
                                    showSecond={false}
                                    format={"HH:mm"}
                                    placeholder="Saat seçin"
                                    onSelect={value => setFieldValue("startHour", value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col sm={6} xs={24}>
                            <Form.Item label="Bitiş Saati">
                                <TimePicker
                                    style={{width: "100%"}}
                                    name="endHour"
                                    value={values.endHour ? moment(values.endHour, "HH:mm") : ""}
                                    showNow={false}
                                    showSecond={false}
                                    format={"HH:mm"}
                                    placeholder="Saat seçin"
                                    onSelect={value => setFieldValue("endHour", value)}
                                />
                            </Form.Item>
                        </Col>
                        <Col sm={12} xs={24}>
                            <Form.Item label="Kampanya Durumu">
                                <Select
                                    name="status"
                                    value={values.status}
                                    onChange={value => setFieldValue("status", value)}
                                >
                                    {Object.values(courierBonusCampaignStatuses).map(status => (
                                        <Select.Option key={status.value} value={status.value}>
                                            {status.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col sm={24} xs={24}>
                            <Form.Item label="Günler" style={{marginBottom: 0}}>
                                <CampaignDaysInput
                                    value={values.days}
                                    setValue={value => setFieldValue("days", value)}
                                />
                            </Form.Item>
                        </Col>
                        <Divider />

                        {values.type === courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value && (
                            <Col sm={24} xs={24}>
                                <Form.Item>
                                    <MinimumPackagesInput
                                        value={
                                            values.conditions[
                                                courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value
                                            ]?.courierAmountsByPackageCount?.map(item => ({
                                                minPackageCount: item.minimumPackageCount,
                                                paymentAmount: item.courierAmount
                                            })) || []
                                        }
                                        setValue={value => {
                                            const formattedValue = value.map(item => ({
                                                minimumPackageCount: parseInt(
                                                    item.minPackageCount,
                                                    10
                                                ),
                                                courierAmount: parseInt(item.paymentAmount, 10)
                                            }));

                                            setFieldValue(
                                                `conditions.${courierBonusCampaignTypes.PACKAGE_BONUS_FEE.value}.courierAmountsByPackageCount`,
                                                formattedValue
                                            );
                                        }}
                                    />
                                </Form.Item>
                            </Col>
                        )}
                        {values.type === courierBonusCampaignTypes.HOURLY_FEE.value && (
                            <>
                                <Col sm={24} xs={24}>
                                    <Form.Item label="Kurye Tutarı">
                                        <InputNumber
                                            style={{width: "100%"}}
                                            name={`${values?.type}.courierAmount`}
                                            type="number"
                                            value={
                                                values?.conditions?.[values?.type]?.courierAmount
                                            }
                                            onChange={value => {
                                                setFieldValue(
                                                    `conditions.${values?.type}.courierAmount`,
                                                    value
                                                );
                                            }}
                                            min="0"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col sm={24} xs={24}>
                                    <Form.Item label="Firma Tutarı">
                                        <InputNumber
                                            style={{width: "100%"}}
                                            name={`${values?.type}.companyAmount`}
                                            type="number"
                                            value={
                                                values?.conditions?.[values?.type]?.companyAmount
                                            }
                                            onChange={value => {
                                                setFieldValue(
                                                    `conditions.${values?.type}.companyAmount`,
                                                    value
                                                );
                                            }}
                                            min="0"
                                        />
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                        {values.type === courierBonusCampaignTypes.HOURLY_GUARANTEE_FEE.value && (
                            <>
                                <Col sm={24} xs={24}>
                                    <Form.Item>
                                        <Checkbox
                                            checked={
                                                values?.conditions?.[values?.type]?.chargeSubCompany
                                            }
                                            onChange={e => {
                                                const isChecked = e.target.checked;
                                                setFieldValue(
                                                    `conditions.${values?.type}.chargeSubCompany`,
                                                    isChecked
                                                );
                                                if (!isChecked) {
                                                    setFieldValue(
                                                        `conditions.${values?.type}.subCompanyGuaranteeAmount`,
                                                        undefined
                                                    );
                                                    setFieldValue(
                                                        `conditions.${values?.type}.subCompanyId`,
                                                        undefined
                                                    );
                                                    if (formik.errors?.conditions?.[values?.type]) {
                                                        formik.setErrors({
                                                            ...formik.errors,
                                                            conditions: {
                                                                ...formik.errors.conditions,
                                                                [values?.type]: {
                                                                    ...formik.errors.conditions[
                                                                        values?.type
                                                                    ],
                                                                    subCompanyGuaranteeAmount: undefined,
                                                                    subCompanyId: undefined
                                                                }
                                                            }
                                                        });
                                                    }
                                                }
                                            }}
                                        >
                                            Müşteriden Ücret Alınacak mı?
                                        </Checkbox>
                                    </Form.Item>
                                </Col>
                                {values?.conditions?.[values?.type]?.chargeSubCompany && (
                                    <>
                                        <Col sm={24} xs={24}>
                                            <Form.Item
                                                label="Müşteri Garanti Ücreti"
                                                validateStatus={
                                                    formik.errors?.conditions?.[values?.type]
                                                        ?.subCompanyGuaranteeAmount
                                                        ? "error"
                                                        : ""
                                                }
                                                help={
                                                    formik.errors?.conditions?.[values?.type]
                                                        ?.subCompanyGuaranteeAmount
                                                }
                                            >
                                                <InputNumber
                                                    style={{width: "100%"}}
                                                    name={`conditions.${values?.type}.subCompanyGuaranteeAmount`}
                                                    type="number"
                                                    value={
                                                        values?.conditions?.[values?.type]
                                                            ?.subCompanyGuaranteeAmount
                                                    }
                                                    onChange={value => {
                                                        setFieldValue(
                                                            `conditions.${values?.type}.subCompanyGuaranteeAmount`,
                                                            value
                                                        );
                                                        if (
                                                            formik.errors?.conditions?.[
                                                                values?.type
                                                            ]?.subCompanyGuaranteeAmount
                                                        ) {
                                                            formik.setErrors({
                                                                ...formik.errors,
                                                                conditions: {
                                                                    ...formik.errors.conditions,
                                                                    [values?.type]: {
                                                                        ...formik.errors.conditions[
                                                                            values?.type
                                                                        ],
                                                                        subCompanyGuaranteeAmount: undefined
                                                                    }
                                                                }
                                                            });
                                                        }
                                                    }}
                                                    min="0"
                                                />
                                            </Form.Item>
                                        </Col>
                                        <Col sm={24} xs={24}>
                                            <Form.Item
                                                label="Ücret Kesilecek Alt Firma"
                                                validateStatus={
                                                    formik.errors?.conditions?.[values?.type]
                                                        ?.subCompanyId
                                                        ? "error"
                                                        : ""
                                                }
                                                help={
                                                    formik.errors?.conditions?.[values?.type]
                                                        ?.subCompanyId
                                                }
                                            >
                                                <Select
                                                    style={{width: "100%"}}
                                                    showSearch
                                                    placeholder="Alt firma seçin"
                                                    loading={subCompaniesLoading}
                                                    name={`conditions.${values?.type}.subCompanyId`}
                                                    value={
                                                        values?.conditions?.[values?.type]
                                                            ?.subCompanyId
                                                    }
                                                    onChange={value => {
                                                        setFieldValue(
                                                            `conditions.${values?.type}.subCompanyId`,
                                                            value
                                                        );
                                                        if (
                                                            formik.errors?.conditions?.[
                                                                values?.type
                                                            ]?.subCompanyId
                                                        ) {
                                                            formik.setErrors({
                                                                ...formik.errors,
                                                                conditions: {
                                                                    ...formik.errors.conditions,
                                                                    [values?.type]: {
                                                                        ...formik.errors.conditions[
                                                                            values?.type
                                                                        ],
                                                                        subCompanyId: undefined
                                                                    }
                                                                }
                                                            });
                                                        }
                                                    }}
                                                    filterOption={(input, option) =>
                                                        option.children
                                                            .toLowerCase()
                                                            .indexOf(input.toLowerCase()) >= 0
                                                    }
                                                >
                                                    {(Array.isArray(subCompanies)
                                                        ? subCompanies
                                                        : []
                                                    )
                                                        ?.filter(subCompany => {
                                                            return (
                                                                subCompany.creditMode !== "passive"
                                                            );
                                                        })
                                                        .map(subCompany => (
                                                            <Select.Option
                                                                key={subCompany._id}
                                                                value={subCompany._id}
                                                            >
                                                                {subCompany.name}
                                                            </Select.Option>
                                                        ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                    </>
                                )}
                                <Col sm={24} xs={24}>
                                    <Form.Item label="Kurye Garanti Ücreti">
                                        <InputNumber
                                            style={{width: "100%"}}
                                            name={`conditions.${values?.type}.courierGuaranteeAmount`}
                                            type="number"
                                            value={
                                                values?.conditions?.[values?.type]
                                                    ?.courierGuaranteeAmount
                                            }
                                            onChange={value => {
                                                setFieldValue(
                                                    `conditions.${values?.type}.courierGuaranteeAmount`,
                                                    value
                                                );
                                            }}
                                            min="0"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col sm={24} xs={24}>
                                    <Form.Item label="Hesaplamaya Dahil Etmek İstediğiniz Faktörler">
                                        <Select
                                            style={{width: "100%"}}
                                            name={`conditions.${values?.type}.calculationFactors`}
                                            mode="multiple"
                                            value={
                                                values?.conditions?.[values?.type]
                                                    ?.calculationFactors
                                            }
                                            onChange={value => {
                                                setFieldValue(
                                                    `conditions.${values?.type}.calculationFactors`,
                                                    value
                                                );
                                            }}
                                        >
                                            {Object.values(guaranteeCalculationFactors).map(
                                                factor => (
                                                    <Select.Option
                                                        key={factor.value}
                                                        value={factor.value}
                                                    >
                                                        {factor.label}
                                                    </Select.Option>
                                                )
                                            )}
                                        </Select>
                                    </Form.Item>
                                </Col>
                            </>
                        )}
                    </Row>
                </Form>
            </Modal>
        </React.Fragment>
    );
};
